import React, { useEffect, useRef, useState, useMemo } from 'react'
import SwiperCore, { Controller } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  useComparatore,
  useFindMenu,
  useTrans,
  useUtente,
  useTealium,
  useIsMobile,
} from '@next-nx/hooks'
import { getHeaderHeight, scrollTo } from '@next-nx/utils/safe'
import { Breadcrumbs, CustomLdJson, WContainer } from '@next-nx/shared-ui/atoms'
import { CircleBox } from '@next-nx/shared-ui/molecules'
import {
  CompareBar,
  ConfiguratoreOfferta,
  FaqSection,
  FormRichiestaPreventivoModal,
  HelpBanner,
  LandingReassurance,
  Page,
  ProdottiCorrelati,
  ProdottoAmbientato,
  ProdottoCaratteristiche,
  ProdottoDisclaimer,
  ProdottoHero,
  ProdottoServiziInclusi,
  RichiestaPreventivoThankYouModal,
  VantaggiSection,
} from '@next-nx/shared-ui/organisms'
import styles from './ProdottoTemplate.module.sass'
import { OffertaType } from '@next-nx/utils/types'
import { useCreateRichiestaContattoMutation } from '@next-nx/shared-graphql/unipolrental'
import { getCategoriaUrlParam, utentePrivato, getTimestamp } from '@next-nx/utils'
import { useRouter } from 'next/router'
import { ProductJsonLd } from 'next-seo'
import { DOMAIN } from '@next-nx/utils/settings'

SwiperCore.use([Controller])

interface Props {
  offerta: OffertaType
  pagina?: any
}

const ProdottoTemplate = (props: Props) => {
  const { offerta, pagina = {} } = props

  const router = useRouter()
  const t = useTrans()
  const isMobile = useIsMobile()
  const heroRef = useRef(null)
  const sectionRefs = useRef([])
  const sectionLinksRef = useRef(null)
  const { utente } = useUtente()
  const [scrollY, setScrollY] = useState<number>(0)
  const [activeSection, setActiveSection] = useState<number | null>(null)
  const [sectionLinksSticky, setSectionLinksSticky] = useState(false)
  const toolbarPresente = utente && (utente.isStaff || utente.isImpersonate)
  const [stickySwiper, setStickySwiper] = useState<any | null>(null)

  const { firstTime } = useComparatore()
  const [compareBarCollapsed, setCompareBarCollapsed] = useState(false) // Da istruzioni: al primo atterraggio visibile

  const scrollToRef = (ref) => {
    if (ref) scrollTo(ref, toolbarPresente, 10 + (sectionLinksSticky ? 48 : 0))
  }

  const [openFormRichiestaPreventivoModal, setOpenFormRichiestaPreventivoModal] = useState(false)
  const [openRichiestaPreventivoThankYouModal, setOpenRichiestaPreventivoThankYouModal] =
    useState(false)
  const [formRichiestaPreventivoInitialData, setFormRichiestaPreventivoInitialData] = useState({
    utentePrivato: utentePrivato(offerta.categoria?.codice),
  })
  const [statusRichiestaPreventivoThankYouModal, setStatusRichiestaPreventivoThankYouModal] =
    useState<string | undefined>()

  const [createRichiestaContattoMutation] = useCreateRichiestaContattoMutation()

  const offertaMenu = useFindMenu('pagina-pdp')
  const serviziMenu = useFindMenu('servizi', offertaMenu)
  const disclaimerMenu = useFindMenu('disclaimer', offertaMenu)
  const boxConfigurazioneMenu = useFindMenu('configurazione', offertaMenu)
  const faqsMenu = useFindMenu('domande', offertaMenu)
  const vantaggiMenu = useFindMenu('vantaggi', offertaMenu)
  const correlatiMenu = useFindMenu('correlati', offertaMenu)
  const reassuranceMenu = useFindMenu('reassurance', offertaMenu)
  const helpBannerMenu = useFindMenu('help_banner')
  const helpBannerListingMenu = useFindMenu('listing-pdp', helpBannerMenu)

  const jsonAllImages = useMemo(() => {
    let dynamicImages = offerta.veicolo.immaginiDinamiche ? offerta.veicolo.immaginiDinamiche : []
    return offerta.veicolo.immaginiStatiche
      ? dynamicImages.concat(offerta.veicolo.immaginiStatiche)
      : dynamicImages
  }, [offerta])

  const { linkDataLayer, getProdottoData } = useTealium()

  const sections: string[] = [
    ...(offerta ? ['Caratteristiche'] : []),
    ...(serviziMenu ? ['Servizi inclusi'] : []),
    ...(offerta.veicolo.mainImmagineDinamica ? ['Immagini'] : []),
    ...(offerta.faqs?.length ? ['Domande'] : []),
  ]

  const handleScroll = () => {
    setScrollY(window.scrollY)
  }

  const handleTracking = (
    action_type: string = '',
    interazione: string = '',
    detail_item?: OffertaType
  ) => {
    let dlObj = {}
    const action_timestamp = getTimestamp()

    switch (interazione) {
      case 'configurazione_personalizzata':
        dlObj = {
          virtual: 'action',
          action_name: 'configurazione_personalizzata',
          action_tech: 'click',
          action_type: 'richiedi_configurazione_personalizzata',
          action_timestamp,
          action_effect: 'richiedi_configurazione_personalizzata',
        }
        break
      case 'info_prodotti':
        dlObj = {
          virtual: 'action',
          action_name: 'info_prodotti',
          action_tech: 'click',
          action_type: action_type.replace(/ /g, '_').toLocaleLowerCase(),
          action_timestamp,
          action_effect: 'informazioni_su_prodotto',
        }
        break
      case 'item_detail':
        dlObj = {
          virtual: 'action',
          action_name: 'item_detail',
          action_tech: 'view',
          action_type: 'pdp',
          action_timestamp,
          action_effect: 'visualizzazione_dettaglio_prodotto',
          ...(detail_item && {
            detail_item: [getProdottoData(detail_item, detail_item.categoria?.codice)],
          }),
        }
        break
      default:
        dlObj
        break
    }
    linkDataLayer(dlObj)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleTracking('', 'item_detail', offerta)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const headerHeight = getHeaderHeight(false)
    const sticky =
      !!sectionLinksRef?.current &&
      scrollY >
        sectionLinksRef.current.offsetTop + sectionLinksRef.current.clientHeight - headerHeight
    const topHeight = headerHeight + (sticky ? sectionLinksRef.current.clientHeight : 0)
    const activeSectionIndex =
      !sectionRefs?.current ||
      scrollY >
        sectionRefs.current[sections.length - 1].offsetTop +
          sectionRefs.current[sections.length - 1].clientHeight -
          topHeight
        ? null
        : sectionRefs.current
            ?.slice(0, sections.length)
            .reduce((acc, x, i) => (x && scrollY > x.offsetTop - topHeight - 15 ? i : acc), 0)

    setActiveSection(activeSectionIndex)
    setSectionLinksSticky(sticky)
    if ((activeSectionIndex || activeSectionIndex === 0) && stickySwiper) {
      stickySwiper.slideTo(activeSectionIndex)
    }
  }, [scrollY])

  return (
    <Page pagina={pagina} item={offerta} className={styles.root}>
      <WContainer>
        <Breadcrumbs breadcrumbs={pagina.breadcrumbs} className={styles.breadcrumbs} />
      </WContainer>
      <div className={styles.heroBox} ref={heroRef}>
        <ProdottoHero offerta={offerta} />
      </div>
      <WContainer>
        <div className={styles.layout}>
          <div className={styles.main}>
            {sections.length > 0 && (
              <div className={styles.sectionLinksPlaceholder} ref={sectionLinksRef}>
                <div
                  className={`${styles.sectionLinks} ${
                    sectionLinksSticky ? styles['sectionLinks--sticky'] : ''
                  }`}
                >
                  <div className={styles.sectionLinksContainer}>
                    <div className={styles.sectionLinksContent}>
                      <Swiper slidesPerView={'auto'} onSwiper={(s) => setStickySwiper(s)}>
                        {sections.map((x, i) => (
                          <SwiperSlide key={`section-${i}`}>
                            <button
                              key={i}
                              className={`${styles.sectionLink} ${
                                activeSection === i ? styles['sectionLink--active'] : ''
                              }`}
                              onClick={() => {
                                scrollToRef(sectionRefs?.current?.[i])
                                handleTracking(x, 'info_prodotti')
                              }}
                            >
                              {t(x)}
                            </button>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {!!offerta && (
              <div ref={(element) => sectionRefs.current.push(element)}>
                <ProdottoCaratteristiche offerta={offerta} className={styles.caratteristiche} />
              </div>
            )}
            {!!serviziMenu && (
              <div ref={(element) => sectionRefs.current.push(element)}>
                <ProdottoServiziInclusi item={serviziMenu} className={styles.serviziInclusi} />
              </div>
            )}
            {!!disclaimerMenu?.description && (
              <ProdottoDisclaimer text={disclaimerMenu.description} className={styles.disclaimer} />
            )}
            {boxConfigurazioneMenu && (
              <div className={styles.configurazioneBox}>
                <CircleBox
                  item={{ ...boxConfigurazioneMenu, image: offerta.veicolo.mainImmagineStatica }}
                  onClick={() => {
                    setOpenFormRichiestaPreventivoModal(true)
                    handleTracking('', 'configurazione_personalizzata')
                  }}
                  className={styles.configurazione}
                  ctaClassName={'form_richiesta_preventivo'.toHotjarFormat('{0}', isMobile)}
                />
              </div>
            )}
            {!!offerta.veicolo.mainImmagineDinamica && (
              <div ref={(element) => sectionRefs.current.push(element)}>
                <ProdottoAmbientato
                  item={{
                    name: offerta.veicolo.titoloSezioneAmbientata || offerta.veicolo.nome,
                    description: offerta.veicolo.descrizioneSezioneAmbientata || '',
                    image: offerta.veicolo.mainImmagineDinamica,
                  }}
                  className={styles.ambientato}
                />
              </div>
            )}
          </div>
          <div
            className={styles.sidebar}
            style={{
              marginTop: heroRef?.current?.clientHeight
                ? heroRef.current.clientHeight * -1 + 24
                : 0,
            }}
          >
            <ConfiguratoreOfferta
              offerta={offerta}
              autoDelMese={offerta.autoMese}
              senzaAnticipo={!!router?.query?.senza_anticipo || offerta.anticipoZero}
              onClick={({ utentePrivato }) => {
                setFormRichiestaPreventivoInitialData({
                  ...formRichiestaPreventivoInitialData,
                  utentePrivato,
                })
                setOpenFormRichiestaPreventivoModal(true)
              }}
              className={styles.configuratore}
            />
          </div>
        </div>
      </WContainer>
      <div className={styles.bottom}>
        {!!faqsMenu && !!offerta.faqs?.length && (
          <div ref={(element) => sectionRefs.current.push(element)}>
            <FaqSection faqs={offerta.faqs} item={faqsMenu} className={styles.faqs} />
          </div>
        )}
        {!!vantaggiMenu && <VantaggiSection item={vantaggiMenu} />}
        {!!correlatiMenu && (
          <ProdottiCorrelati
            item={correlatiMenu}
            offerte={offerta.offerteCorrelate}
            codiceCategoriaPrimaria={offerta.categoria?.codice}
            showCompareTooltip={firstTime}
            className={styles.correlati}
          />
        )}
        {!!reassuranceMenu && (
          <div className={styles.reassurance}>
            <WContainer>
              <LandingReassurance item={reassuranceMenu} />
            </WContainer>
          </div>
        )}
        {!!helpBannerListingMenu && <HelpBanner item={helpBannerListingMenu} />}
      </div>
      <FormRichiestaPreventivoModal
        utentePrivato={formRichiestaPreventivoInitialData?.utentePrivato}
        modelloVeicolo={offerta.nome}
        tipologiaVeicolo={offerta.veicolo.categoria?.nome}
        open={openFormRichiestaPreventivoModal}
        setOpen={setOpenFormRichiestaPreventivoModal}
        mutation={createRichiestaContattoMutation}
        offerta={offerta}
        onSubmit={(status) => {
          setOpenFormRichiestaPreventivoModal(false)
          setOpenRichiestaPreventivoThankYouModal(true)
          setStatusRichiestaPreventivoThankYouModal(status)
        }}
      />
      <RichiestaPreventivoThankYouModal
        open={openRichiestaPreventivoThankYouModal}
        setOpen={setOpenRichiestaPreventivoThankYouModal}
        status={statusRichiestaPreventivoThankYouModal}
      />
      <CompareBar
        type="pdp"
        collapsed={compareBarCollapsed}
        setCollapsed={(v) => setCompareBarCollapsed(v)}
        url={'/comparatore' + getCategoriaUrlParam(offerta.categoria?.codice)}
        className={styles.compareBar}
      />
      <ProductJsonLd
        productName={offerta.veicolo.alias}
        images={jsonAllImages}
        description={offerta.descrizione}
        brand={offerta.veicolo.marca.nome}
        mpn={offerta.veicolo.codiceInfocar}
        offers={[
          {
            price: offerta.prezzoIe
              ? offerta.prezzoIe.toString()
              : offerta.prezzoIi
              ? offerta.prezzoIi.toString()
              : '0',
            priceCurrency: 'EUR',
            itemCondition:
              offerta.condizioniKm === 0
                ? 'https://schema.org/NewCondition'
                : 'https://schema.org/UsedCondition',
            availability: 'https://schema.org/InStock',
            url: DOMAIN + '/auto/' + offerta.slug,
            seller: { name: 'Unipolrental' },
          },
        ]}
      />
      <CustomLdJson
        type="Car"
        objectId={offerta.pk}
        object={{
          '@context': 'https://schema.org/',
          '@type': 'Car',
          'name': offerta.veicolo.alias,
          'vehicleIdentificationNumber': offerta.veicolo.codiceInfocar,
          'steeringPosition': {
            '@type': 'SteeringPositionValue',
            '@id': 'https://schema.org/LeftHandDriving',
          },
          'emissionsCO2': offerta.veicolo.ecoImpactCo2,
          'vehicleTransmission': offerta.veicolo.cambio,
        }}
      />
    </Page>
  )
}

export default ProdottoTemplate
